import { whatsappReviewDraft, WHATSAPP_REVIEW_TTL_MS } from "./whatsappReviewHandoff.ts";

const KEY = "whatsapp_review_v1";
const PREFIX = "wa-review:v1:";
const MAX = 20;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
export const WHATSAPP_REVIEW_MESSAGE_COLUMNS = "id,user_id,session_id,role,content,channel,created_at,source_item_id";
const COLUMNS = "id,user_id,storage_path,original_filename,mime_sniffed,byte_size,kind,status,extracted_text,extracted_meta,created_at,deleted_at";
type Row = Record<string, unknown>;
export interface WhatsAppReviewAttachment { id: string; fileName: string; byteSize: number; kind: "image" | "pdf" | "sheet" }
export interface WhatsAppReviewStage {
  messageId: string; ownerId: string; expiresAt: number; text: string; attachments: WhatsAppReviewAttachment[];
}
export interface WhatsAppReviewLink { id: string; read_revision: string; created_at: string }
interface Manifest {
  version: 1; marker: string; messageId: string; ownerId: string; sessionId: string;
  textHash: string; createdAt: string; expiresAt: number; link: WhatsAppReviewLink;
  inventory: { id: string; fingerprint: string }[];
}
// The metadata column is writable only by service_role. This relation is written
// only from the authenticated webhook's already-ingested media IDs, never a model,
// URL or app payload. A source_item_id marker by itself grants no attachment access.
export interface WhatsAppReviewDatabase { from(table: string): any; rpc(name: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: unknown }> }
const object = (value: unknown): value is Row => !!value && typeof value === "object" && !Array.isArray(value);
const uuid = (value: unknown): value is string => typeof value === "string" && UUID.test(value);
const marker = (value: unknown): value is string => typeof value === "string" && value.startsWith(PREFIX) && uuid(value.slice(PREFIX.length));

export function strictWhatsAppReviewIds(value: unknown, max = MAX): string[] | null {
  if (!Array.isArray(value) || value.length < 1 || value.length > max || value.some(id => !uuid(id))) return null;
  return new Set(value).size === value.length ? value as string[] : null;
}
function canonical(value: unknown, depth = 0): string {
  if (depth > 12) throw new Error("metadata_depth");
  if (Array.isArray(value)) return "[" + value.map(item => canonical(item, depth + 1)).join(",") + "]";
  if (object(value)) return "{" + Object.keys(value).sort().map(key => JSON.stringify(key) + ":" + canonical(value[key], depth + 1)).join(",") + "}";
  const result = JSON.stringify(value);
  if (result === undefined) throw new Error("invalid_metadata");
  return result;
}
async function digest(value: string): Promise<string> {
  const bytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, "0")).join("");
}
async function fingerprint(row: Row): Promise<string> {
  if (!object(row.extracted_meta)) throw new Error("invalid_metadata");
  const meta = { ...row.extracted_meta }; delete meta[KEY];
  const metaText = canonical(meta);
  if (metaText.length > 65_536 || (typeof row.extracted_text === "string" && row.extracted_text.length > 1_000_000)) throw new Error("snapshot_too_large");
  return digest(canonical({
    id: row.id, owner: row.user_id, storage: row.storage_path, filename: row.original_filename,
    mime: row.mime_sniffed, bytes: row.byte_size, kind: row.kind, created: row.created_at,
    text: row.extracted_text, meta,
  }));
}
function ready(row: Row, owner: string): boolean {
  return uuid(row.id) && row.user_id === owner && row.status === "ready" && row.deleted_at === null &&
    typeof row.storage_path === "string" && row.storage_path.startsWith(owner + "/") &&
    typeof row.original_filename === "string" && row.original_filename.length > 0 && row.original_filename.length <= 512 &&
    typeof row.mime_sniffed === "string" && Number.isSafeInteger(row.byte_size) && Number(row.byte_size) > 0 &&
    ["image", "pdf", "sheet"].includes(String(row.kind)) && typeof row.created_at === "string" && object(row.extracted_meta);
}
function descriptor(row: Row): WhatsAppReviewAttachment {
  return { id: String(row.id), fileName: String(row.original_filename), byteSize: Number(row.byte_size), kind: row.kind as WhatsAppReviewAttachment["kind"] };
}
function expiry(message: Row): number { return Date.parse(String(message.created_at)) + WHATSAPP_REVIEW_TTL_MS; }
function live(message: Row, now: number): boolean {
  const created = Date.parse(String(message.created_at));
  return Number.isFinite(created) && created <= now + 5000 && expiry(message) > now;
}
async function deadline<T>(value: PromiseLike<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try { return await Promise.race([Promise.resolve(value), new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error("review_timeout")), ms); })]); }
  finally { if (timer) clearTimeout(timer); }
}
async function maxPerTurn(db: WhatsAppReviewDatabase): Promise<number> {
  const result = await db.from("agent_config").select("value").eq("key", "executor.max_attachments_per_turn").maybeSingle();
  if (result.error) throw new Error("review_config_unavailable");
  const count = Number(result.data?.value);
  return Number.isFinite(count) && count >= 1 ? Math.min(MAX, Math.floor(count)) : 10;
}
async function linkCurrent(db: WhatsAppReviewDatabase, owner: string, link: WhatsAppReviewLink): Promise<boolean> {
  if (!uuid(link.id) || !uuid(link.read_revision) || typeof link.created_at !== "string") return false;
  const result = await deadline(db.from("whatsapp_links").select("id,read_revision,created_at")
    .eq("id", link.id).eq("user_id", owner).eq("active", true).maybeSingle(), 5000);
  const data = (result as any).data;
  return !(result as any).error && data?.id === link.id && data?.read_revision === link.read_revision && data?.created_at === link.created_at;
}

/** Resolve the complete protected inventory; no URLs, bytes, provider IO or writes. */
export async function readWhatsAppAttachmentReview(options: {
  db: WhatsAppReviewDatabase; ownerId: string; messageId: string;
  verify: () => Promise<boolean>; authSessionId: string | null; now?: () => number; signal?: AbortSignal;
}): Promise<WhatsAppReviewStage | null> {
  const now = options.now ?? Date.now, start = now();
  let message: Row | null = null;
  const current = async () => !options.signal?.aborted && now() - start < 15_000 &&
    (!message || live(message, now())) && await deadline(options.verify(), 5000) &&
    !options.signal?.aborted && (!message || live(message, now()));
  const checked = async <T>(operation: () => PromiseLike<T>): Promise<T> => {
    if (!await current()) throw new Error("review_revoked");
    const result = await deadline(operation(), 5000);
    if (!await current()) throw new Error("review_revoked");
    return result;
  };
  try {
    if (!uuid(options.ownerId) || !uuid(options.messageId)) return null;
    const result = await checked(() => options.db.from("mirror_messages").select(WHATSAPP_REVIEW_MESSAGE_COLUMNS)
      .eq("id", options.messageId).eq("user_id", options.ownerId).eq("role", "user").eq("channel", "whatsapp").maybeSingle()) as any;
    if (result.error || !object(result.data)) return null;
    message = result.data;
    const session = await checked(() => options.db.from("mirror_sessions").select("id,user_id,kind")
      .eq("id", message!.session_id).eq("user_id", options.ownerId).eq("kind", "whatsapp").maybeSingle()) as any;
    const text = session.error ? null : whatsappReviewDraft(message, session.data, { ownerId: options.ownerId, messageId: options.messageId, now: now() });
    if (text === null) return null;
    if (message.source_item_id === null || message.source_item_id === undefined) {
      return await current() ? { messageId: options.messageId, ownerId: options.ownerId, expiresAt: expiry(message), text, attachments: [] } : null;
    }
    if (!marker(message.source_item_id)) return null;
    const cap = await checked(() => maxPerTurn(options.db));
    // Query by protected server relation, never IDs nominated by an app/model.
    const rowsResult = await checked(() => options.db.from("executor_attachments").select(COLUMNS)
      .eq("user_id", options.ownerId).eq("extracted_meta->whatsapp_review_v1->>messageId", options.messageId).limit(MAX + 1)) as any;
    if (rowsResult.error || !Array.isArray(rowsResult.data) || rowsResult.data.length < 1 || rowsResult.data.length > cap) return null;
    const rows: Row[] = rowsResult.data;
    if (rows.some(row => !ready(row, options.ownerId))) return null;
    const relation = rows[0].extracted_meta as Row;
    if (!object(relation[KEY])) return null;
    const manifest = relation[KEY] as unknown as Manifest;
    const ids = strictWhatsAppReviewIds(manifest.inventory?.map(item => item.id), cap);
    if (!ids || rows.length !== ids.length || new Set(rows.map(row => row.id)).size !== ids.length ||
      manifest.version !== 1 || manifest.marker !== message.source_item_id || manifest.messageId !== message.id ||
      manifest.ownerId !== options.ownerId || manifest.sessionId !== message.session_id ||
      manifest.createdAt !== message.created_at || manifest.expiresAt !== expiry(message) ||
      manifest.textHash !== await checked(() => digest(text)) || !object(manifest.link)) return null;
    const expected = canonical(manifest);
    const ordered: Row[] = [];
    for (const item of manifest.inventory) {
      const row = rows.find(row => row.id === item.id);
      if (!row || !object(row.extracted_meta) || canonical(row.extracted_meta[KEY]) !== expected ||
        typeof item.fingerprint !== "string" || item.fingerprint !== await checked(() => fingerprint(row))) return null;
      ordered.push(row);
    }
    if (!await checked(() => linkCurrent(options.db, options.ownerId, manifest.link))) return null;
    // Re-read the turn after all IO: editing/removing its marker cannot race redemption.
    const final = await checked(() => options.db.from("mirror_messages").select(WHATSAPP_REVIEW_MESSAGE_COLUMNS)
      .eq("id", options.messageId).eq("user_id", options.ownerId).maybeSingle()) as any;
    if (final.error || canonical(final.data) !== canonical(message)) return null;
    const finalRows = await checked(() => options.db.from("executor_attachments").select(COLUMNS)
      .eq("user_id", options.ownerId).eq("extracted_meta->whatsapp_review_v1->>messageId", options.messageId).limit(MAX + 1)) as any;
    if (finalRows.error || !Array.isArray(finalRows.data) || finalRows.data.length !== rows.length ||
      finalRows.data.some((row: Row) => !rows.some(old => old.id === row.id && canonical(old) === canonical(row))) || !await current()) return null;
    if (options.authSessionId !== null && !uuid(options.authSessionId)) return null;
    // The final RPC observes all authority/row predicates at one DB decision,
    // instead of moving origin/inventory races between alternating network reads.
    // Only service_role can call it; APP supplies the actual verified JWT SID.
    const snapshot = await deadline(options.db.rpc("verify_whatsapp_attachment_review_snapshot", {
      p_owner: options.ownerId, p_auth_session: options.authSessionId,
      p_snapshot: { message, session: session.data, link: manifest.link, rows },
    }), 5000);
    if (snapshot.error || !object(snapshot.data) || snapshot.data.ok !== true ||
      options.signal?.aborted || !live(message, now()) || now() - start >= 15_000) return null;
    return { messageId: options.messageId, ownerId: options.ownerId, expiresAt: expiry(message), text, attachments: ordered.map(descriptor) };
  } catch { return null; }
}

/** Server webhook only. CAS preserves extraction metadata and never renews turn TTL. */
export async function bindWhatsAppReviewAttachments(options: {
  db: WhatsAppReviewDatabase; message: Row; session: unknown; ownerId: string;
  attachmentIds: string[]; link: WhatsAppReviewLink; now?: () => number;
}): Promise<boolean> {
  const now = options.now ?? Date.now, start = now();
  let message = options.message;
  const verify = async () => live(message, now()) && now() - start < 15_000 && await linkCurrent(options.db, options.ownerId, options.link) && live(message, now());
  const checked = async <T>(operation: () => PromiseLike<T>): Promise<T> => {
    if (!await verify()) throw new Error("review_revoked");
    const value = await deadline(operation(), 5000);
    if (!await verify()) throw new Error("review_revoked");
    return value;
  };
  try {
    const text = whatsappReviewDraft(message, options.session, { ownerId: options.ownerId, messageId: String(message.id), now: now() });
    const cap = await checked(() => maxPerTurn(options.db));
    const ids = strictWhatsAppReviewIds(options.attachmentIds, cap);
    if (text === null || !ids) return false;
    if (marker(message.source_item_id)) {
      const existing = await readWhatsAppAttachmentReview({ db: options.db, ownerId: options.ownerId, messageId: String(message.id), verify, authSessionId: null, now });
      return !!existing && canonical(existing.attachments.map(row => row.id)) === canonical(ids);
    }
    if (message.source_item_id !== null && message.source_item_id !== undefined) return false;
    const rowsResult = await checked(() => options.db.from("executor_attachments").select(COLUMNS)
      .eq("user_id", options.ownerId).in("id", ids).limit(MAX + 1)) as any;
    if (rowsResult.error || !Array.isArray(rowsResult.data) || rowsResult.data.length !== ids.length ||
      new Set(rowsResult.data.map((row: Row) => row.id)).size !== ids.length) return false;
    const rows: Row[] = ids.map(id => rowsResult.data.find((row: Row) => row.id === id));
    if (rows.some(row => !row || !ready(row, options.ownerId) || Object.prototype.hasOwnProperty.call(row.extracted_meta, KEY))) return false;
    const inventory = [];
    for (const row of rows) inventory.push({ id: String(row.id), fingerprint: await checked(() => fingerprint(row)) });
    const source = PREFIX + crypto.randomUUID();
    const claim = await checked(() => options.db.from("mirror_messages").update({ source_item_id: source })
      .eq("id", message.id).eq("user_id", options.ownerId).eq("session_id", message.session_id)
      .eq("role", "user").eq("channel", "whatsapp").eq("content", text).eq("created_at", message.created_at)
      .is("source_item_id", null).select(WHATSAPP_REVIEW_MESSAGE_COLUMNS).maybeSingle()) as any;
    if (claim.error || !object(claim.data) || claim.data.source_item_id !== source) return false;
    message = claim.data;
    const manifest: Manifest = {
      version: 1, marker: source, messageId: String(message.id), ownerId: options.ownerId,
      sessionId: String(message.session_id), textHash: await checked(() => digest(text)),
      createdAt: String(message.created_at), expiresAt: expiry(message), link: options.link, inventory,
    };
    for (const row of rows) {
      const saved = await checked(() => options.db.from("executor_attachments")
        .update({ extracted_meta: { ...row.extracted_meta as Row, [KEY]: manifest } })
        .eq("id", row.id).eq("user_id", options.ownerId).eq("status", "ready").is("deleted_at", null)
        .eq("storage_path", row.storage_path).eq("extracted_meta", canonical(row.extracted_meta))
        .select("id").maybeSingle()) as any;
      if (saved.error || saved.data?.id !== row.id) return false;
    }
    const stage = await readWhatsAppAttachmentReview({ db: options.db, ownerId: options.ownerId, messageId: String(message.id), verify, authSessionId: null, now });
    return !!stage && canonical(stage.attachments.map(row => row.id)) === canonical(ids);
  } catch { return false; }
}

/** Strict explicit subset; the full original inventory was already revalidated. */
export function whatsappReviewSubset(stage: WhatsAppReviewStage, value: unknown, now = Date.now()): string[] | null {
  const ids = strictWhatsAppReviewIds(value);
  return now < stage.expiresAt && ids && ids.every(id => stage.attachments.some(row => row.id === id)) ? ids : null;
}
